class BeforeYouLeave extends HTMLElement {
  constructor() {
    super();
    this.popup = this.querySelector('.modal--popup');
    this.overlay = this.querySelector('[id^="Modal-Overlay-"]');
    this.content = this.querySelector('.popup__inner');
    
    // We'll use this to ensure it only fires once per session
    this.sessionKey = 'before-you-leave-triggered';
    this.hasTriggered = false;

    this.querySelector('.popup__close')?.addEventListener('click', () => this.setClosePopup());
    this.overlay?.addEventListener('click', () => this.setClosePopup());

    if (Shopify.designMode) {
      document.addEventListener('shopify:section:select', e => {
        if (e.target.matches('.section-before-you-leave')) this.setOpenPopup();
      });
      document.addEventListener('shopify:section:deselect', e => {
        if (e.target.matches('.section-before-you-leave')) this.setClosePopup();
      });
    }
  }

  connectedCallback() {
    if (!this.popup) return;

    // Don't initialize if already shown this session or if in Shopify Editor
    if (sessionStorage.getItem(this.sessionKey) || Shopify.designMode) return;

    this.initExitIntent();
  }

  initExitIntent() {
    // 1. DESKTOP: Mouse leaving viewport at the top
    this.boundMouseLeave = (e) => {
      if (e.clientY <= 0) {
        this.triggerPopup();
      }
    };
    document.addEventListener('mouseleave', this.boundMouseLeave);

    // 2. MOBILE: Detect "Scroll Up" behavior
    let lastScrollTop = 0;
    this.boundScroll = () => {
      let st = window.pageYOffset || document.documentElement.scrollTop;
      // If user scrolls UP more than 15px and has scrolled down at least 150px
      if (lastScrollTop - st > 15 && st > 150) {
        this.triggerPopup();
      }
      lastScrollTop = st <= 0 ? 0 : st;
    };
    window.addEventListener('scroll', this.boundScroll, { passive: true });
  }

  triggerPopup() {
    if (this.hasTriggered) return;
    
    this.hasTriggered = true;
    sessionStorage.setItem(this.sessionKey, 'true'); // Mark as shown
    this.setOpenPopup();
    
    // Cleanup listeners once triggered
    this.removeEventListeners();
  }

  removeEventListeners() {
    document.removeEventListener('mouseleave', this.boundMouseLeave);
    window.removeEventListener('scroll', this.boundScroll);
  }

  disconnectedCallback() {
    this.removeEventListeners();
  }

  setOpenPopup() {
    if (!this.popup) return;
    this.popup.setAttribute('open', 'true');
    this.animateOpen();
    document.body.classList.add('overflow-hidden');
    document.documentElement.setAttribute('scroll-lock', '');
  }

  setClosePopup() {
    if (!this.popup) return;
    this.animateClose();
    setTimeout(() => {
      this.popup.removeAttribute('open');
      document.body.classList.remove('overflow-hidden');
      document.documentElement.removeAttribute('scroll-lock');
    }, 100);
  }

  animateOpen() {
    if (typeof Motion !== 'undefined') {
      Motion.timeline([
        [this.content, { opacity: [0, 1], transform: ['translate(-50%, -40%)', 'translate(-50%, -50%)'] }, { duration: 0.3, at: '-0.15' }]
      ]).finished;
    }
  }

  animateClose() {
    if (typeof Motion !== 'undefined') {
      Motion.timeline([
        [this.content, { opacity: [1, 0], transform: ['translate(-50%, -50%)', 'translate(-50%, -40%)'] }, { duration: 0.2 }]
      ]).finished;
    }
  }
}

if (!customElements.get('before-you-leave')) customElements.define('before-you-leave', BeforeYouLeave);

// --- COPY CODE COMPONENT (Kept unchanged as requested) ---
class CopyCode extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('button');
    this.tooltip = this.querySelector('.tooltip');
    this.resetTooltipTimer = null;
    this.input = this.querySelector('input');
    this.resetInputTimer = null;
  }
  
  connectedCallback() {
    if (this.button) {
      this.button.addEventListener('click', this.copyCode.bind(this));
    }
  }

  copyCode() {
    const text = this.getAttribute('data-text');
    if (!text) return;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
    }
    this.showSuccess();
  }

  showSuccess() {
    const defaultMessage = window.theme?.strings?.copy || 'Copy';
    const defaultInputValue = this.input?.value ?? '';

    if (this.tooltip && this.tooltip.dataset.success_message) {
      this.tooltip.textContent = this.tooltip.dataset.success_message;
      clearTimeout(this.resetTooltipTimer);
      this.resetTooltipTimer = setTimeout(() => {
        this.tooltip.textContent = defaultMessage;
      }, 1500);
    }

    if (this.input) {
      this.input.value = 'Copied!';
      clearTimeout(this.resetInputTimer);
      this.resetInputTimer = setTimeout(() => {
        this.input.value = defaultInputValue;
      }, 1500);
    }

    this.classList.add('copied');
    setTimeout(() => {
      this.classList.remove('copied');
    }, 1500);
  }
}
if (!customElements.get('copy-code')) customElements.define('copy-code', CopyCode);